// Records a voice message from the microphone and returns it as an MP3 data URL.
// Browsers record WebM/Opus, which WhatsApp rejects, so the audio is re-encoded to MP3 (supported everywhere).
// The MP3 encoder (public/lame.min.js) is a plain script, loaded the first time it is needed.

const MAX_SECONDS = 5 * 60;

let encoderLoading = null;
const loadEncoder = () => {
  if (window.lamejs && window.lamejs.Mp3Encoder) return Promise.resolve();
  if (!encoderLoading) {
    encoderLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `${process.env.PUBLIC_URL || ""}/lame.min.js`;
      s.onload = () => (window.lamejs && window.lamejs.Mp3Encoder ? resolve() : reject(new Error("encoder missing")));
      s.onerror = () => { encoderLoading = null; reject(new Error("encoder failed to load")); };
      document.head.appendChild(s);
    });
  }
  return encoderLoading;
};

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

const toMp3DataUrl = async (blob) => {
  await loadEncoder();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  let audio;
  try {
    audio = await ctx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    ctx.close && ctx.close();
  }

  // Mono is plenty for speech and keeps the file small.
  const input = audio.getChannelData(0);
  const pcm = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const v = Math.max(-1, Math.min(1, input[i]));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }

  const encoder = new window.lamejs.Mp3Encoder(1, audio.sampleRate, 64);
  const chunks = [];
  const BLOCK = 1152;
  for (let i = 0; i < pcm.length; i += BLOCK) {
    const out = encoder.encodeBuffer(pcm.subarray(i, i + BLOCK));
    if (out.length) chunks.push(out);
  }
  const tail = encoder.flush();
  if (tail.length) chunks.push(tail);

  const mp3 = new Blob(chunks, { type: "audio/mpeg" });
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(mp3);
  });
};

// Asks for the microphone and starts recording. Resolves to { stop, cancel }:
//   stop()   -> Promise of the MP3 data URL
//   cancel() -> throws the recording away
// onAutoStop fires if the time limit is reached (the recording is kept; call stop() to get it).
export const startVoiceRecording = async ({ onAutoStop } = {}) => {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    throw new Error("unsupported");
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = pickMimeType();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const parts = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) parts.push(e.data); };

  const releaseMic = () => stream.getTracks().forEach((t) => t.stop());
  const finished = new Promise((resolve) => { recorder.onstop = resolve; });
  recorder.start();

  let limitTimer = setTimeout(() => { if (onAutoStop) onAutoStop(); }, MAX_SECONDS * 1000);
  const clear = () => { clearTimeout(limitTimer); limitTimer = null; };

  return {
    stop: async () => {
      clear();
      if (recorder.state !== "inactive") recorder.stop();
      await finished;
      releaseMic();
      const blob = new Blob(parts, { type: recorder.mimeType || mimeType || "audio/webm" });
      if (!blob.size) throw new Error("empty");
      return toMp3DataUrl(blob);
    },
    cancel: () => {
      clear();
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
      releaseMic();
    },
  };
};

export const VOICE_MAX_SECONDS = MAX_SECONDS;
